import { ChevronRight, FileText, Globe, ListChecks, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useInternalLink } from "@/hooks/use-internal-link";
import { type LinkedInBatchSummary } from "@/types/linkedin";
import { cn } from "@/lib/utils";

// Rótulos do breakdown de aprovação dos aplicados ('manual' = revisão humana).
const APROVACAO_LABELS: Record<string, string> = {
  manual: "manual",
  freelancer_declarado: "auto: freelancer declarado",
  sem_emprego_atual: "auto: sem emprego atual",
  empresa_reconhecida_ia: "auto: empresa reconhecida",
  empresa_cadastrada: "auto: empresa cadastrada",
  auto: "automática",
};

function formatDataLote(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return "";
  }
}

function caminhoDoLote(batchId: string | null): string {
  return `/linkedin/lote/${batchId ?? "avulsos"}`;
}

interface Props {
  batch: LinkedInBatchSummary;
  onReviewSequential: (batchId: string) => void;
}

/**
 * Cartão de um lote na listagem: só o resumo (o trabalho acontece na tela do
 * lote). O cartão inteiro navega pra lá; "Revisar em sequência" atalha direto
 * pra fila de revisão.
 */
export function LinkedInBatchCard({ batch, onReviewSequential }: Props) {
  const linkProps = useInternalLink();
  const destino = caminhoDoLote(batch.batch_id);
  const ehBusca = batch.source === "scraping";
  const unidade = ehBusca ? "perfil" : "arquivo";
  const progressPct =
    batch.total > 0
      ? Math.round(((batch.total - batch.processing) / batch.total) * 100)
      : 0;

  return (
    <div
      {...linkProps(destino)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.click();
      }}
      className="flex cursor-pointer flex-col gap-3 rounded-lg border bg-card p-3 transition-colors hover:border-primary/40 hover:bg-accent/40 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-semibold">
            {batch.batch_id
              ? `Lote · ${formatDataLote(batch.created_last)}`
              : "Uploads avulsos"}
          </span>
          <Badge
            variant="outline"
            className={cn(
              "shrink-0",
              ehBusca
                ? "border-sky-200 bg-sky-50 text-sky-800"
                : "bg-muted text-muted-foreground",
            )}
          >
            {ehBusca ? (
              <Globe className="mr-1 h-3 w-3" />
            ) : (
              <FileText className="mr-1 h-3 w-3" />
            )}
            {ehBusca ? "Busca automática" : "Upload de PDFs"}
          </Badge>
          <Badge variant="secondary" className="shrink-0">
            {batch.total} {unidade}
            {batch.total === 1 ? "" : "s"}
          </Badge>
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
          {batch.review_ready > 0 && (
            <span className="text-primary">{batch.review_ready} pra revisar</span>
          )}
          {batch.processing > 0 && (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              {batch.processing} processando
            </span>
          )}
          {batch.awaiting_match > 0 && (
            <span className="text-amber-700">
              {batch.awaiting_match} aguardando associação
            </span>
          )}
          {batch.failed > 0 && (
            <span className="text-red-700">{batch.failed} com falha</span>
          )}
          {batch.concluidos > 0 && <span>{batch.concluidos} concluídos</span>}
        </div>

        {batch.aprovacoes.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 pt-0.5">
            {batch.aprovacoes.map((aprovacao) => (
              <Badge
                key={aprovacao.code}
                variant="outline"
                className={cn(
                  "text-[10px]",
                  aprovacao.code === "manual"
                    ? "border-transparent bg-muted text-muted-foreground"
                    : "border-emerald-200 bg-emerald-50 text-emerald-800",
                )}
              >
                {aprovacao.count} {APROVACAO_LABELS[aprovacao.code] ?? aprovacao.code}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        {batch.processing > 0 && (
          <Progress value={progressPct} className="h-1.5 w-24" />
        )}
        {batch.batch_id && batch.review_ready > 0 && (
          <Button
            size="sm"
            className="gap-1.5"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onReviewSequential(batch.batch_id!);
            }}
          >
            <ListChecks className="h-4 w-4" />
            Revisar em sequência ({batch.review_ready})
          </Button>
        )}
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
    </div>
  );
}
